import { useRef, useState } from 'react';
import { ASSETS } from '../config/site';
import { useLocale } from '../context/LocaleContext';
import ScrollExpand from '../components/reactbits/ScrollExpand/ScrollExpand';
import { FAQSection, FinalCTA, PageHero, StepsSection } from '../components/shared/PageSections';
import { SectionIntro } from '../components/ui/Primitives';

function SchoolHeroVisual() {
  return (
    <div className="people-orbit" aria-hidden="true">
      <span className="people-orbit__line" />
      <div className="people-orbit__person people-orbit__person--main"><img src={ASSETS.comicBoySchool} alt="" /></div>
      <div className="people-orbit__person people-orbit__person--secondary"><img src={ASSETS.comicGirl} alt="" /></div>
      <span className="people-orbit__count">+20</span>
    </div>
  );
}

function SchoolOverview({ content }) {
  const images = [ASSETS.schoolOverview, ASSETS.schoolPersonality, ASSETS.schoolInvolved];
  return (
    <section className="section-shell school-overview">
      <SectionIntro eyebrow={content.eyebrow} title={content.title} />
      <div className="school-overview__grid" data-reveal="stagger">
        {content.features.map((feature, index) => (
          <article key={feature.title} className="school-feature-card" style={{ '--stagger': index }}>
            <div className="school-feature-card__copy">
              <span>{String(index + 1).padStart(2, '0')}</span>
              <h3>{feature.title}</h3>
              <p>{feature.description}</p>
            </div>
            <div className="school-feature-card__media"><img src={images[index]} alt={feature.title} loading="lazy" /></div>
          </article>
        ))}
      </div>
    </section>
  );
}

function SchoolJourney({ content, onlineLabel }) {
  const [active, setActive] = useState(0);
  const tabRefs = useRef([]);
  const phase = content.phases[active];

  const selectTab = (index) => {
    const next = (index + content.phases.length) % content.phases.length;
    setActive(next);
    window.requestAnimationFrame(() => tabRefs.current[next]?.focus());
  };

  const onTabKeyDown = (event, index) => {
    const keyActions = {
      ArrowRight: () => selectTab(index + 1),
      ArrowDown: () => selectTab(index + 1),
      ArrowLeft: () => selectTab(index - 1),
      ArrowUp: () => selectTab(index - 1),
      Home: () => selectTab(0),
      End: () => selectTab(content.phases.length - 1),
    };
    if (!keyActions[event.key]) return;
    event.preventDefault();
    keyActions[event.key]();
  };

  return (
    <section className="school-journey section-shell">
      <SectionIntro eyebrow={content.eyebrow} title={content.title} />
      <div className="school-journey__tabs" role="tablist" aria-label={content.title}>
        {content.phases.map((item, index) => (
          <button
            key={item.phase}
            id={`school-phase-tab-${index}`}
            ref={(node) => { tabRefs.current[index] = node; }}
            type="button"
            role="tab"
            aria-selected={active === index}
            aria-controls="school-journey-panel"
            tabIndex={active === index ? 0 : -1}
            onClick={() => setActive(index)}
            onKeyDown={(event) => onTabKeyDown(event, index)}
          >
            <span>{item.phase}</span><strong>{item.title}</strong>
          </button>
        ))}
      </div>

      <div className="school-journey__stage" id="school-journey-panel" role="tabpanel" aria-labelledby={`school-phase-tab-${active}`} key={active}>
        <div className="chat-card glass-card">
          <div className="chat-card__header">
            <img src="/assets/original/profile-assistant-icon.svg" alt="" />
            <div><strong>Coach Anna</strong><span>{onlineLabel}</span></div>
          </div>
          <div className="chat-card__messages">
            {phase.dialogue.map((message, index) => (
              <p key={`${message.speaker}-${index}`} data-speaker={message.speaker}>{message.text}</p>
            ))}
          </div>
        </div>
        <div className="school-journey__document">
          <img src={active === 2 ? ASSETS.exampleCv : ASSETS.recruitingOverview} alt="" />
          <div className="school-journey__tags">
            {phase.tags.map((tag) => <span key={tag}>{tag}</span>)}
          </div>
        </div>
      </div>
    </section>
  );
}

export default function SchoolsPage() {
  const { t } = useLocale();
  const schools = t.schools;

  return (
    <main id="main-content">
      <PageHero
        eyebrow={schools.hero.eyebrow}
        title={schools.hero.title}
        description={schools.hero.subtitle}
        primaryLabel={schools.hero.primaryCta}
        secondaryLabel={schools.hero.secondaryCta}
        secondaryTo="https://calendly.com/your-applicant/schulen"
        visual={<SchoolHeroVisual />}
        className="page-hero--schools"
      />

      <SchoolOverview content={schools.overview} />

      <section className="school-scroll-story" aria-label={schools.overview.title}>
        <ScrollExpand
          src={ASSETS.tablet}
          alt={t.common.aria.teacherDashboard}
          title={schools.journey.title}
          scrollHint={t.common.labels.scroll}
          startWidth={48}
          startHeight={62}
          startRadius={28}
          endRadius={24}
          mediaZoom={1.04}
          mediaFit="contain"
          scrollDistance={0.7}
          holdDistance={0.12}
          smoothing={0.045}
          overlayScrim={0.46}
          stageOffset={92}
          activationOffset={0.34}
          useWindowScroll
        >
          <span className="scroll-story__eyebrow">{schools.overview.eyebrow}</span>
          <h2>{schools.overview.title}</h2>
        </ScrollExpand>
      </section>

      <SchoolJourney content={schools.journey} onlineLabel={t.common.labels.online} />
      <StepsSection
        eyebrow={schools.steps.eyebrow}
        title={schools.steps.title}
        items={schools.steps.items.map((item) => ({ title: item.title, text: item.description }))}
        primaryLabel={t.common.actions.register}
        secondaryLabel={t.common.actions.viewPricing}
      />
      <FAQSection
        eyebrow={schools.faq.eyebrow}
        title={schools.faq.title}
        items={schools.faq.items}
        contact={{
          text: schools.faq.contactLead,
          label: schools.faq.contactCta,
          href: 'https://calendly.com/your-applicant/schulen',
          emailLead: schools.faq.emailLead,
          email: 'schulen@yourapplicant.ch',
        }}
      />
      <FinalCTA {...{
        eyebrow: t.home.closing.eyebrow,
        title: t.home.closing.title,
        text: t.home.closing.description,
        buttonLabel: t.home.closing.cta,
      }} />
    </main>
  );
}
